import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
  Card,
  CardContent,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material';
import React, { useContext } from 'react';
import {
  DownloadRounded,
  RemoveRedEyeRounded,
  ModeEditOutlineRounded,
  Save,
  Archive,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import URLS from '../../services/urlConstants';
import { get } from '../../services/apiMethods';
import { downloadExcel } from '../../utils/utilities';
import ShowComponent from '../ShowComponent';
import WarehouseContext from '../../context/WarehouseContext';
import ThemeModeContext from '../../context/ThemeModeContext';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: { xs: 11, sm: 12 },
    fontWeight: 500,
    padding: { xs: '2px 6px', sm: '4px 8px' },
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
    }`,
    minHeight: { xs: '28px', sm: '32px' },
    verticalAlign: 'middle',
  },
  [`&.${tableCellClasses.head}`]: {
    fontSize: { xs: 12, sm: 13 },
    fontWeight: 700,
    padding: { xs: '4px 6px', sm: '6px 8px' },
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.87)',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    borderBottom: `2px solid #4ecdc4`,
    backdropFilter: 'blur(8px)',
    boxShadow:
      theme.palette.mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
    minHeight: { xs: '32px', sm: '36px' },
    verticalAlign: 'middle',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function sumValues(data) {
  let sum = 0;

  for (const category of data) {
    category.data?.forEach(item => {
      sum += item.count;
    });
  }

  return sum;
}

export default function SavedStock({ data }) {
  const { selectedOwnerCode, selectedWarehouse, selectedOwnerGroup } = useContext(WarehouseContext);
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [downloadingReport, setDownloadingReport] = React.useState([]);
  const [downloadingAll, setDownloadingAll] = React.useState(false);

  const navigate = useNavigate();
  const handleView = async (id, k) => {
    try {
      navigate(`/edit/saved/stock/${id}`, { state: { itemData: k } });
    } catch (error) {
      console.log(error);
    }
  };

  const handleViewAll = async k => {
    try {
      navigate(`/view/all/saved/stock`, { state: { itemData: k } });
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownloadReport = async subFamily => {
    if (subFamily) {
      setDownloadingReport([...downloadingReport, subFamily]);
    } else {
      setDownloadingAll(true);
    }
    try {
      let filter = {};
      if (subFamily) {
        filter['subFamily'] = subFamily;
      }
      const { data } = await get(URLS.items.downloadSavedStockReport, {
        params: {
          ...filter,
          warehouses: selectedWarehouse,
          ownerCodes: selectedOwnerCode,
          ownerGroups: selectedOwnerGroup,
        },
        responseType: 'blob',
      });
      downloadExcel(data, 'SAVED_STOCK.xlsx');
      if (subFamily) {
        setDownloadingReport(downloadingReport.filter(s => s === subFamily));
      } else {
        setDownloadingAll(false);
      }
    } catch (error) {
      console.log(error);
      if (subFamily) {
        setDownloadingReport(downloadingReport.filter(s => s === subFamily));
      } else {
        setDownloadingAll(false);
      }
    }
  };
  return (
    <Fade in timeout={500}>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          borderRadius: { xs: 2, sm: 2.5 },
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.2s ease',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Header Section */}
          <Box
            sx={{
              p: { xs: 1, sm: 1.5 },
              borderBottom: `1px solid ${
                isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
              }`,
              background: 'linear-gradient(135deg, #4ecdc412 0%, #4ecdc406 100%)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #4ecdc4 0%, #44b3a6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #4ecdc430',
                }}
              >
                <Save sx={{ fontSize: { xs: 16, sm: 18 }, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    color: '#4ecdc4',
                    mb: 0.25,
                  }}
                >
                  Saved Stock Inventory
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, display: 'block' }}
                >
                  Reserved items for future allocation
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              disabled={!data.length}
              onClick={() => handleViewAll(data)}
              startIcon={<RemoveRedEyeRounded sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                py: { xs: 0.5, sm: 0.75 },
                px: { xs: 1, sm: 1.5 },
                bgcolor: '#4ecdc4',
                color: 'white',
                '&:hover': {
                  bgcolor: '#44b3a6',
                },
                '&:disabled': {
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                },
              }}
            >
              View All
            </Button>
          </Box>

          {/* Table Content */}
          <TableContainer>
            <Table>
              <TableBody>
                {data?.map((d, index) => (
                  <TableRow key={d.category}>
                    <StyledTableCell
                      sx={{
                        borderRight: `1px solid ${
                          isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                        }`,
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        color: '#4ecdc4',
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                        width: { xs: '140px', sm: '160px' },
                        minWidth: { xs: '140px', sm: '160px' },
                        maxWidth: { xs: '140px', sm: '160px' },
                        padding: { xs: '6px 4px', sm: '8px 6px' },
                        verticalAlign: 'top',
                      }}
                      align="center"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: { xs: 0.5, sm: 0.75 },
                        }}
                      >
                        <Archive sx={{ fontSize: { xs: 14, sm: 16 }, color: '#4ecdc4' }} />
                        {d.category}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell sx={{ p: 0 }}>
                      <TableContainer>
                        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <StyledTableCell
                                align="center"
                                sx={{
                                  width: { xs: '50%', sm: '55%' },
                                  minWidth: { xs: '120px', sm: '140px' },
                                  maxWidth: { xs: '120px', sm: '140px' },
                                }}
                              >
                                Description
                              </StyledTableCell>
                              <StyledTableCell
                                align="center"
                                sx={{
                                  width: { xs: '25%', sm: '20%' },
                                  minWidth: { xs: '60px', sm: '70px' },
                                  maxWidth: { xs: '60px', sm: '70px' },
                                }}
                              >
                                Quantity
                              </StyledTableCell>
                              <StyledTableCell
                                align="center"
                                sx={{
                                  width: { xs: '25%', sm: '25%' },
                                  minWidth: { xs: '80px', sm: '90px' },
                                  maxWidth: { xs: '80px', sm: '90px' },
                                }}
                              >
                                Actions
                              </StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {d.data?.map((k, i) => (
                              <StyledTableRow key={k.subFamilyId}>
                                <StyledTableCell
                                  align="center"
                                  sx={{
                                    width: { xs: '50%', sm: '55%' },
                                    minWidth: { xs: '120px', sm: '140px' },
                                    maxWidth: { xs: '120px', sm: '140px' },
                                    padding: { xs: '2px 4px', sm: '4px 6px' },
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                      lineHeight: 1.1,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      width: '100%',
                                      display: 'block',
                                    }}
                                    title={
                                      selectedWarehouse?.length !== 1
                                        ? `${k.subFamilyName} - (${k.warehouseCode})`
                                        : `${k.subFamilyName}`
                                    }
                                  >
                                    {selectedWarehouse?.length !== 1
                                      ? `${k.subFamilyName} - (${k.warehouseCode})`
                                      : `${k.subFamilyName}`}
                                  </Typography>
                                </StyledTableCell>
                                <StyledTableCell
                                  align="center"
                                  sx={{
                                    width: { xs: '25%', sm: '20%' },
                                    minWidth: { xs: '60px', sm: '70px' },
                                    maxWidth: { xs: '60px', sm: '70px' },
                                    padding: { xs: '2px 4px', sm: '4px 6px' },
                                  }}
                                >
                                  <Chip
                                    label={k.count}
                                    size="small"
                                    variant="filled"
                                    color="secondary"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                      height: { xs: 16, sm: 18 },
                                      minWidth: { xs: '28px', sm: '32px' },
                                      bgcolor: '#4ecdc4',
                                      color: 'white',
                                      '& .MuiChip-label': {
                                        px: { xs: 0.25, sm: 0.5 },
                                        py: 0,
                                      },
                                    }}
                                  />
                                </StyledTableCell>
                                <StyledTableCell
                                  align="center"
                                  sx={{
                                    width: { xs: '25%', sm: '25%' },
                                    minWidth: { xs: '80px', sm: '90px' },
                                    maxWidth: { xs: '80px', sm: '90px' },
                                    padding: { xs: '2px 4px', sm: '4px 6px' },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      gap: { xs: 0.25, sm: 0.5 },
                                      flexWrap: 'nowrap',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <ShowComponent action={'view'} module="Inventory">
                                      <IconButton
                                        onClick={() => handleDownloadReport(k.subFamilyId)}
                                        disabled={
                                          !k.count || downloadingReport.includes(k.subFamilyId)
                                        }
                                        size="small"
                                        sx={{
                                          width: { xs: 20, sm: 24 },
                                          height: { xs: 20, sm: 24 },
                                          minWidth: { xs: 20, sm: 24 },
                                          bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                          '&:hover': {
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.15)'
                                              : 'rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                          },
                                          transition: 'all 0.2s ease',
                                          padding: 0,
                                        }}
                                      >
                                        {downloadingReport.includes(k.subFamilyId) ? (
                                          <CircularProgress
                                            size={{ xs: 10, sm: 12 }}
                                            sx={{ color: '#4ecdc4' }}
                                          />
                                        ) : (
                                          <DownloadRounded
                                            sx={{
                                              fontSize: { xs: 10, sm: 12 },
                                              color: '#4ecdc4',
                                            }}
                                          />
                                        )}
                                      </IconButton>
                                    </ShowComponent>
                                    <ShowComponent action={'view'} module="Inventory">
                                      <Tooltip title="View Details" arrow>
                                        <IconButton
                                          disabled={!k.count}
                                          onClick={() => handleView(k.subFamilyId, k)}
                                          size="small"
                                          sx={{
                                            width: { xs: 20, sm: 24 },
                                            height: { xs: 20, sm: 24 },
                                            minWidth: { xs: 20, sm: 24 },
                                            bgcolor: isDarkMode
                                              ? 'rgba(255,255,255,0.1)'
                                              : 'rgba(0,0,0,0.05)',
                                            '&:hover': {
                                              bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.15)'
                                                : 'rgba(0,0,0,0.08)',
                                              transform: 'translateY(-1px)',
                                            },
                                            transition: 'all 0.2s ease',
                                            padding: 0,
                                          }}
                                        >
                                          <RemoveRedEyeRounded
                                            sx={{
                                              fontSize: { xs: 10, sm: 12 },
                                              color: '#4ecdc4',
                                            }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    </ShowComponent>
                                  </Box>
                                </StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>

              {/* Enhanced Footer */}
              <TableFooter>
                <StyledTableRow
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(78, 205, 196, 0.1)' : 'rgba(78, 205, 196, 0.05)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(78, 205, 196, 0.15)' : 'rgba(78, 205, 196, 0.08)',
                    },
                  }}
                >
                  <StyledTableCell
                    sx={{
                      fontWeight: 700,
                      color: '#4ecdc4',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 } }}>
                      <Archive sx={{ fontSize: { xs: 14, sm: 16 } }} />
                      Total Saved Stock
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="right" sx={{ pr: 3 }}>
                    <Chip
                      label={sumValues(data)}
                      variant="filled"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        minWidth: { xs: '50px', sm: '60px' },
                        height: { xs: '28px', sm: '32px' },
                        bgcolor: '#4ecdc4',
                        color: 'white',
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Download Complete Report
                  </StyledTableCell>
                  <StyledTableCell align="right" sx={{ pr: 3 }}>
                    <ShowComponent action={'view'} module="Inventory">
                      <IconButton
                        onClick={() => handleDownloadReport()}
                        disabled={!sumValues(data) || downloadingAll}
                        sx={{
                          width: { xs: 32, sm: 36 },
                          height: { xs: 32, sm: 36 },
                          bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          border: `1px solid ${
                            isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
                          }`,
                          '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {downloadingAll ? (
                          <CircularProgress size={{ xs: 14, sm: 16 }} sx={{ color: '#4ecdc4' }} />
                        ) : (
                          <DownloadRounded
                            sx={{
                              fontSize: { xs: 14, sm: 16 },
                              color: '#4ecdc4',
                            }}
                          />
                        )}
                      </IconButton>
                    </ShowComponent>
                  </StyledTableCell>
                </StyledTableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Fade>
  );
}
